import { useState, useEffect, useCallback } from 'react';
import supabase from './supabaseClient';
import {
  filterToAllowedClients,
  filterToLockedClients,
  getClientAccessInfo,
} from './utils/clientAccess';
import { checkSubscriptionStatus } from './utils/subscriptionUtils';
import { calculateLoggingStreak, type MealRow } from './utils/clientStatsUtils';

interface ClientCard {
  telegramUserId: string;
  clientName: string;
  weeklySuccessRate: number;
  currentStreak: number;
  lastLoggedDate: string | null;
  status: 'on-track' | 'midway' | 'struggling';
  totalMeals: number;
  profilePictureUrl?: string;
}

interface LockedClientCard {
  telegramUserId: string;
  clientName: string;
}

interface DashboardOverviewProps {
  onClientSelect: (userId: string, clientName: string) => void;
  selectedUserId: string;
  onNavigate?: (view: 'overview' | 'clients' | 'meals' | 'insights' | 'leaderboard' | 'subscription') => void;
}

export default function DashboardOverview({ onClientSelect, selectedUserId, onNavigate }: DashboardOverviewProps) {
  const [clientCards, setClientCards] = useState<ClientCard[]>([]);
  const [lockedClientCards, setLockedClientCards] = useState<LockedClientCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'on-track' | 'midway' | 'struggling'>('all');

  const fetchClientOverview = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get current trainer's clients
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: clients, error: clientsError } = await supabase
        .from('trainers_clients')
        .select('*')
        .eq('trainer_id', user.id);

      if (clientsError) {
        console.error('Error fetching clients:', clientsError);
        return;
      }

      if (!clients || clients.length === 0) {
        setClientCards([]);
        setLockedClientCards([]);
        return;
      }

      const isPremium = await checkSubscriptionStatus(user.id);
      const { allowedTelegramIds } = await getClientAccessInfo(user.id, isPremium);
      const visibleClients = filterToAllowedClients(clients, allowedTelegramIds);
      const lockedClients = filterToLockedClients(clients, allowedTelegramIds);

      setLockedClientCards(
        lockedClients.map((client) => ({
          telegramUserId: client.telegram_user_id,
          clientName: client.client_name || `User: ${client.telegram_user_id}`,
        }))
      );

      // Get overview data for each active client
      const clientOverview: ClientCard[] = [];

      for (const client of visibleClients) {
        // Get client's recent meals (fetch 30 days by created_at; we'll filter by meal_time)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data: meals, error: mealsError } = await supabase
          .from('Mastersheet')
          .select('*')
          .eq('telegram_user_id', client.telegram_user_id)
          .gte('created_at', thirtyDaysAgo.toISOString())
          .order('created_at', { ascending: false });

        if (mealsError) {
          console.error('Error fetching meals for client:', client.telegram_user_id, mealsError);
          continue;
        }

        const { data: streakMeals, error: streakError } = await supabase
          .from('Mastersheet')
          .select('meal_time, created_at')
          .eq('telegram_user_id', client.telegram_user_id);

        if (streakError) {
          console.error('Error fetching streak meals for client:', client.telegram_user_id, streakError);
        }

        // Get client's diet goals
        const { data: dietGoals } = await supabase
          .from('client_diet_goals')
          .select('*')
          .eq('telegram_user_id', client.telegram_user_id)
          .single();

        // Get client's profile picture
        const { data: profileData, error: profileError } = await supabase
          .from('client_profiles')
          .select('profile_picture_url')
          .eq('telegram_user_id', client.telegram_user_id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile for client:', client.telegram_user_id, profileError);
        }

        // Calculate weekly success rate
        let weeklySuccessRate = 0;
        if (dietGoals && meals && meals.length > 0) {
          weeklySuccessRate = calculateWeeklySuccessRate(meals, dietGoals);
        }

        // Calculate current streak
        const currentStreak = calculateLoggingStreak((streakMeals || meals || []) as MealRow[]);

        // Get last logged date
        const lastLoggedDate = meals && meals.length > 0 ? meals[0].created_at : null;

        // Determine status
        const status = getClientStatus(weeklySuccessRate, currentStreak, lastLoggedDate);

        clientOverview.push({
          telegramUserId: client.telegram_user_id,
          clientName: client.client_name || `User: ${client.telegram_user_id}`,
          weeklySuccessRate: Math.round(weeklySuccessRate),
          currentStreak,
          lastLoggedDate,
          status,
          totalMeals: meals?.length || 0,
          profilePictureUrl: isPremium ? (profileData?.profile_picture_url || undefined) : undefined
        });
      }

      setClientCards(clientOverview);
    } catch (error) {
      console.error('Error fetching client overview:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClientOverview();
  }, [fetchClientOverview]);

  const calculateWeeklySuccessRate = (meals: any[], dietGoals: any): number => {
    if (!meals.length) return 0;

    // Use meal_time like the leaderboard and filter to last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const recentMeals = meals
      .map(m => ({ ...m, _d: new Date(m.meal_time) }))
      .filter(m => !isNaN(m._d.getTime()) && m._d.getFullYear() > 1971 && m._d >= sevenDaysAgo);

    if (!recentMeals.length) return 0;

    // Group by meal_time date
    const mealsByDate = new Map<string, any[]>();
    recentMeals.forEach(meal => {
      const dateKey = new Date(meal.meal_time).toISOString().slice(0, 10);
      if (!mealsByDate.has(dateKey)) {
        mealsByDate.set(dateKey, []);
      }
      mealsByDate.get(dateKey)!.push(meal);
    });

    let totalSuccess = 0;
    let daysWithMeals = 0;

    mealsByDate.forEach((dayMeals) => {
      const dayCalories = dayMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
      const dayProtein = dayMeals.reduce((sum, meal) => sum + (meal.protein_g || 0), 0);
      const dayCarbs = dayMeals.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0);
      const dayFat = dayMeals.reduce((sum, meal) => sum + (meal.fat_g || 0), 0);

      const caloriesSuccess = dayCalories > 0 ? Math.min(100, (dayCalories / dietGoals.target_calories) * 100) : 0;
      const proteinSuccess = dayProtein > 0 ? Math.min(100, (dayProtein / dietGoals.protein_target) * 100) : 0;
      const carbsSuccess = dayCarbs > 0 ? Math.min(100, (dayCarbs / dietGoals.carbs_target) * 100) : 0;
      const fatSuccess = dayFat > 0 ? Math.min(100, (dayFat / dietGoals.fat_target) * 100) : 0;

      const daySuccess = (caloriesSuccess + proteinSuccess + carbsSuccess + fatSuccess) / 4;
      totalSuccess += daySuccess;
      daysWithMeals++;
    });

    return daysWithMeals > 0 ? totalSuccess / daysWithMeals : 0;
  };

  const getClientStatus = (successRate: number, streak: number, lastLogged: string | null): 'on-track' | 'midway' | 'struggling' => {
    const lastLoggedToday = lastLogged && new Date(lastLogged).toDateString() === new Date().toDateString();
    const lastLoggedYesterday = lastLogged && new Date(lastLogged).toDateString() === new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();
    
    // Primary logic based on success rate and streak
    if (successRate >= 80 && streak >= 3) return 'on-track';
    if (successRate >= 70 && streak >= 1) return 'on-track';
    if (successRate >= 60 || streak >= 1) return 'midway';
    
    // Special cases for recent activity
    if (lastLoggedToday) {
      // If they logged today, they're at least midway
      if (successRate >= 50) return 'midway';
      return 'struggling';
    }
    
    if (lastLoggedYesterday) {
      // If they logged yesterday, they're still engaged
      if (successRate >= 60) return 'midway';
      return 'struggling';
    }
    
    // If they haven't logged in 2+ days, they're struggling
    return 'struggling';
  };

  const getStatusColor = (status: 'on-track' | 'midway' | 'struggling') => {
    switch (status) {
      case 'on-track': return 'bg-[#8aff8a] text-[#1f1f1f]';
      case 'midway': return 'bg-[#ffd700] text-[#1f1f1f]';
      case 'struggling': return 'bg-[#ff6b6b] text-white';
    }
  };

  const getStatusEmoji = (status: 'on-track' | 'midway' | 'struggling') => {
    switch (status) {
      case 'on-track': return '🟢';
      case 'midway': return '🟡';
      case 'struggling': return '🔴';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isNotLoggedToday = (lastLogged: string | null) => {
    if (!lastLogged) return true;
    const today = new Date().toISOString().slice(0, 10);
    const lastLoggedDate = new Date(lastLogged).toISOString().slice(0, 10);
    return lastLoggedDate !== today;
  };

  const filteredClients = clientCards.filter(client => {
    const matchesSearch = client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.telegramUserId.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredLockedClients = lockedClientCards.filter(client => {
    const matchesSearch = client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.telegramUserId.includes(searchTerm);
    return matchesSearch;
  });

  const handleLockedClientClick = (clientName: string) => {
    const confirmed = window.confirm(
      `Access to ${clientName} requires Early Supporter. Subscribe on your iPhone to unlock all clients. Go to subscription page?`
    );
    if (confirmed) {
      onNavigate?.('subscription');
    }
  };

  if (loading) {
    return (
      <div className="retro-card">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">📊 Dashboard Overview</h3>
        </div>
        <div className="p-4 md:p-6">
          <div className="text-center py-8 text-[#2d2d2d]">Loading client overview...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="retro-card dashboard-overview">
      <div className="index-tab">
        <h3 className="text-lg font-semibold text-[#1f1f1f]">📊 Dashboard Overview</h3>
      </div>
      <div className="p-4 md:p-6">
        {/* Status Filter Buttons */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <button
            onClick={() => setStatusFilter('all')}
            className={`rounded-lg p-3 text-center transition-all duration-200 ${
              statusFilter === 'all' 
                ? 'bg-[#0eeaff]/20 border-2 border-[#0eeaff] shadow-lg' 
                : 'bg-white border border-[#d4d4d4] hover:bg-[#f8f4e6]'
            }`}
          >
            <div className="text-2xl font-bold text-[#1f1f1f]">
              {clientCards.length + lockedClientCards.length}
            </div>
            <div className="text-xs text-[#666]">All Clients</div>
          </button>
          
          <button
            onClick={() => setStatusFilter('on-track')}
            className={`rounded-lg p-3 text-center transition-all duration-200 ${
              statusFilter === 'on-track' 
                ? 'bg-[#8aff8a]/20 border-2 border-[#8aff8a] shadow-lg' 
                : 'bg-[#8aff8a]/10 border border-[#8aff8a] hover:bg-[#8aff8a]/20'
            }`}
          >
            <div className="text-2xl font-bold text-[#8aff8a]">
              {clientCards.filter(c => c.status === 'on-track').length}
            </div>
            <div className="text-xs text-[#666]">On Track</div>
          </button>
          
          <button
            onClick={() => setStatusFilter('midway')}
            className={`rounded-lg p-3 text-center transition-all duration-200 ${
              statusFilter === 'midway' 
                ? 'bg-[#ffd700]/20 border-2 border-[#ffd700] shadow-lg' 
                : 'bg-[#ffd700]/10 border border-[#ffd700] hover:bg-[#ffd700]/20'
            }`}
          >
            <div className="text-2xl font-bold text-[#ffd700]">
              {clientCards.filter(c => c.status === 'midway').length}
            </div>
            <div className="text-xs text-[#666]">Midway</div>
          </button>
          
          <button
            onClick={() => setStatusFilter('struggling')}
            className={`rounded-lg p-3 text-center transition-all duration-200 ${
              statusFilter === 'struggling' 
                ? 'bg-[#ff6b6b]/20 border-2 border-[#ff6b6b] shadow-lg' 
                : 'bg-[#ff6b6b]/10 border border-[#ff6b6b] hover:bg-[#ff6b6b]/20'
            }`}
          >
            <div className="text-2xl font-bold text-[#ff6b6b]">
              {clientCards.filter(c => c.status === 'struggling').length}
            </div>
            <div className="text-xs text-[#666]">Needs Attention</div>
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search clients by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="vintage-input w-full max-w-md"
          />
        </div>

        {/* Client Cards Grid */}
        {filteredClients.length === 0 && (statusFilter !== 'all' || filteredLockedClients.length === 0) ? (
          <div className="text-center py-8 text-[#2d2d2d]">
            {searchTerm ? 'No clients found matching your search.' : 'No clients found. Add some clients to see the overview!'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <div
                key={client.telegramUserId}
                className={`bg-white rounded-lg shadow-lg border-2 transition-all duration-300 cursor-pointer hover:scale-105 ${
                  selectedUserId === client.telegramUserId 
                    ? 'border-[#0eeaff] shadow-[#0eeaff]/20' 
                    : 'border-[#d4d4d4] hover:border-[#0eeaff]'
                } ${isNotLoggedToday(client.lastLoggedDate) ? 'animate-pulse shadow-[#ff6b6b]/20' : ''}`}
                onClick={() => onClientSelect(client.telegramUserId, client.clientName)}
              >
                <div className="p-4">
                  {/* Header with Avatar and Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {client.profilePictureUrl ? (
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-[#d4d4d4]">
                          <img 
                            src={client.profilePictureUrl} 
                            alt={`${client.clientName}'s profile`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className="w-full h-full bg-gradient-to-br from-[#0eeaff] to-[#8aff8a] rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg hidden">
                            {getInitials(client.clientName)}
                          </div>
                        </div>
                      ) : (
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#0eeaff] to-[#8aff8a] rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg">
                          {getInitials(client.clientName)}
                        </div>
                      )}
                      <div>
                        <h4 className="font-semibold text-[#1f1f1f] text-sm md:text-base">
                          {client.clientName}
                        </h4>
                        <p className="text-xs text-[#666]">
                          {client.totalMeals} meals this week
                        </p>
                      </div>
                    </div>
                    <div className={`px-1.5 py-0.5 rounded-full text-xs font-medium flex items-center ${getStatusColor(client.status)}`}>
                      <span className="text-xs mr-1">{getStatusEmoji(client.status)}</span>
                      <span className="text-xs">{client.status.replace('-', ' ')}</span>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#666]">Weekly Success</span>
                      <span className={`font-bold text-lg ${
                        client.weeklySuccessRate >= 80 ? 'text-[#8aff8a]' : 
                        client.weeklySuccessRate >= 60 ? 'text-[#ffd700]' : 'text-[#ff6b6b]'
                      }`}>
                        {client.weeklySuccessRate}%
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#666]">Current Streak</span>
                      <span className="font-bold text-[#0eeaff]">
                        {client.currentStreak} {client.currentStreak === 1 ? 'day' : 'days'}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#666]">Last Logged</span>
                      <span className={`text-xs ${
                        isNotLoggedToday(client.lastLoggedDate) ? 'text-[#ff6b6b]' : 'text-[#8aff8a]'
                      }`}>
                        {client.lastLoggedDate 
                          ? new Date(client.lastLoggedDate).toLocaleDateString() 
                          : 'Never'
                        }
                      </span>
                    </div>
                  </div>

                  {/* Quick Action Button */}
                  <div className="mt-3 pt-3 border-t border-[#d4d4d4]">
                    <button 
                      className="w-full bg-[#f8f4e6] hover:bg-[#0eeaff] hover:text-[#1f1f1f] text-[#666] text-xs py-2 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClientSelect(client.telegramUserId, client.clientName);
                        onNavigate?.('meals');
                      }}
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {statusFilter === 'all' && filteredLockedClients.map((client) => (
              <div
                key={client.telegramUserId}
                className="bg-white rounded-lg shadow-lg border-2 border-[#d4d4d4] opacity-90 cursor-pointer hover:border-[#ffd700] relative overflow-hidden"
                onClick={() => handleLockedClientClick(client.clientName)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-[#e8e8e8] rounded-full flex items-center justify-center text-[#888] font-bold text-base md:text-lg">
                        {getInitials(client.clientName)}
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#888] text-sm md:text-base">
                          {client.clientName}
                        </h4>
                      </div>
                    </div>
                    <div className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-[#888] text-white">
                      Locked
                    </div>
                  </div>
                  <div className="py-6 text-center border-t border-[#d4d4d4]">
                    <span className="text-2xl">🔒</span>
                    <p className="text-xs text-[#666] mt-2">
                      Tap to resubscribe and unlock their stats
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}


      </div>
    </div>
  );
} 